import { z } from "zod";

/**
 * The canonical structured service address — the ONE shape both the jobsite
 * capture side and the API persistence/integration side agree on. `formatted`
 * is the verbatim one-line value the user saw (display SSOT); the components are
 * the resolved parts (from Google Places, or left absent on a freeform entry).
 * `source` records how we got it, so downstream code can tell a resolved address
 * from a typed one without guessing.
 */
export const ServiceAddressSchema = z.object({
  formatted: z.string(),
  line1: z.string().optional(),
  line2: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  postalCode: z.string().optional(),
  lat: z.number().optional(),
  lng: z.number().optional(),
  placeId: z.string().optional(),
  source: z.enum(["places", "manual"]),
});

export type ServiceAddress = z.infer<typeof ServiceAddressSchema>;

/**
 * Normalize whatever a submission carries under its `address` key into a
 * `ServiceAddress`, or `null` when there's nothing usable. Accepts:
 *   - a plain string (freeform entry) → `{ formatted, source: "manual" }`
 *   - a structured object (a `ServiceAddress` from the Places-backed field)
 *   - a sloppy object that at least has a `formatted` string → degraded manual
 * Returning `null` for the empty/unusable case lets callers spread-or-skip
 * without branching (a blank field simply contributes no address).
 */
export function toServiceAddress(raw: unknown): ServiceAddress | null {
  if (typeof raw === "string") {
    const formatted = raw.trim();
    return formatted ? { formatted, source: "manual" } : null;
  }
  if (raw && typeof raw === "object") {
    const parsed = ServiceAddressSchema.safeParse(raw);
    if (parsed.success) {
      return parsed.data.formatted.trim() ? parsed.data : null;
    }
    // Tolerate an object that carries a usable formatted line but otherwise
    // doesn't validate — keep the line, drop the untrustworthy parts.
    const formatted = (raw as Record<string, unknown>).formatted;
    if (typeof formatted === "string" && formatted.trim()) {
      return { formatted: formatted.trim(), source: "manual" };
    }
  }
  return null;
}

/**
 * The subset of a Google Places `PlaceResult` we read. Declared structurally so
 * this module (imported by the API too) never depends on `@types/google.maps`;
 * a real `google.maps.places.PlaceResult` satisfies it at the call site.
 */
interface PlaceLike {
  formatted_address?: string;
  address_components?: { types: string[]; long_name: string; short_name: string }[];
  geometry?: { location?: { lat(): number; lng(): number } };
  place_id?: string;
}

/**
 * Extract a ServiceAddress from a Google Places result. The single authority for
 * the address_components → structured-parts mapping (shared with web's address
 * picker; see feedback on unifying parsePlace). Returns null for an unusable
 * place (no formatted address).
 */
export function placeResultToServiceAddress(place: PlaceLike): ServiceAddress | null {
  if (!place.formatted_address) return null;
  const long = (type: string) =>
    place.address_components?.find((c) => c.types.includes(type))?.long_name || undefined;
  const short = (type: string) =>
    place.address_components?.find((c) => c.types.includes(type))?.short_name || undefined;

  const line1 = [long("street_number"), long("route")].filter(Boolean).join(" ") || undefined;
  const lat = place.geometry?.location?.lat();
  const lng = place.geometry?.location?.lng();

  return {
    formatted: place.formatted_address,
    line1,
    line2: long("subpremise"),
    city: long("locality") ?? long("postal_town") ?? long("sublocality"),
    state: short("administrative_area_level_1"),
    postalCode: long("postal_code"),
    lat: typeof lat === "number" ? lat : undefined,
    lng: typeof lng === "number" ? lng : undefined,
    placeId: place.place_id,
    source: "places",
  };
}

/**
 * Project an address onto the provider-neutral source keys an integration
 * FieldMapping reads (`address`, `address_line1`, …). One place owns this
 * mapping so no integration re-encodes the part names. Only present parts are
 * returned, so an absent part becomes an unmapped (omitted) target. `line1`
 * falls back to `formatted`, so an unresolved freeform address still lands in
 * `address_line1` (and thus a CRM's street field) rather than nowhere.
 */
export function flattenForIntegration(a: ServiceAddress | null): Record<string, string> {
  if (!a) return {};
  const out: Record<string, string> = {
    address: a.formatted,
    address_line1: a.line1 ?? a.formatted,
  };
  if (a.line2) out.address_line2 = a.line2;
  if (a.city) out.address_city = a.city;
  if (a.state) out.address_state = a.state;
  if (a.postalCode) out.address_zip = a.postalCode;
  return out;
}
